import { getFooterLinks, type ApiClient, type FooterLink } from '../api';

// Session-lifetime cache: the list is static per operator, so fetch it once
// and share across every consumer (My Packs / Delivery footers, disclaimer
// modal, refund notice). A tiny external store (instead of per-hook state)
// lets components that have no ApiClient of their own — e.g. the disclaimer
// body — still read and react to the loaded list.
let cached: FooterLink[] | null = null;
let inflight: Promise<FooterLink[]> | null = null;
const listeners = new Set<() => void>();

/** `null` while the first fetch is still in flight. */
export function getFooterLinksSnapshot(): FooterLink[] | null {
  return cached;
}

export function subscribeFooterLinks(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Kick off the once-per-session fetch. Deduped: concurrent callers share
 *  one request. Called at app init (ThemedShell) and by the footer hook.
 *  `refresh: true` bypasses the cache and refetches — used when the auth
 *  state changes (embed-mode sign-in) so anonymously fetched links are
 *  replaced with the session's. A superseded request never overwrites the
 *  result of a newer one. Resolves after subscribers have been notified,
 *  so tests can await it. */
export function ensureFooterLinks(
  client: ApiClient,
  { refresh = false }: { refresh?: boolean } = {},
): Promise<FooterLink[]> {
  if (!refresh) {
    if (cached !== null) return Promise.resolve(cached);
    if (inflight) return inflight;
  }
  const request: Promise<FooterLink[]> = getFooterLinks(client).then((result) => {
    if (inflight === request) {
      cached = result;
      listeners.forEach((listener) => listener());
    }
    return result;
  });
  inflight = request;
  return request;
}

/** Test-only: drop the module cache so each test starts cold. */
export function resetFooterLinksCache(): void {
  cached = null;
  inflight = null;
}
